import { ScopeEditor } from "../../costing/ScopeEditor";
import type { ScopeDraft } from "../../costing/types";
import type { JobDraft } from "../lib/jobForm";
import { DocumentsSection } from "./DocumentsSection";
import styles from "./ScopeCostingPage.module.css";

export interface ScopeCostingPageProps {
  draft: JobDraft;
  patch: (partial: Partial<JobDraft>) => void;
  /**
   * Persisted job context for the documents workflow. Absent while minting a
   * new card (the job does not exist server-side yet), which hides the section.
   */
  jobId?: string;
  jobStatus?: string;
  /** Notifies the host when a quote is accepted so board state stays fresh. */
  onQuoteAccepted?: () => void;
}

/**
 * Page 2 of the New Job workflow — Scope.
 *
 * An optional extension: nothing here is required, and removing this page from
 * the workflow leaves the base job fully functional. All state lives in the
 * shared job draft (under `scope`), so navigating between pages keeps every
 * entered value.
 *
 * The editor itself is {@link ScopeEditor}, shared with the standalone invoice
 * builder. What stays here is the job-specific part: the one-shot "Copy from
 * Job Description" action (a copy, not a live sync — the field stays
 * independently editable afterwards) and the documents section, which mounts
 * only for a persisted job.
 */
export function ScopeCostingPage({ draft, patch, jobId, jobStatus, onQuoteAccepted }: ScopeCostingPageProps) {
  const jobDescriptionEmpty = draft.description.trim().length === 0;

  const handleScopeChange = (scope: ScopeDraft): void => patch({ scope });
  const handleCopyFromJob = (): void => patch({ scope: { ...draft.scope, description: draft.description } });

  return (
    <>
      <ScopeEditor
        value={draft.scope}
        onChange={handleScopeChange}
        itemsEmptyMessage="No line items yet. Without them the job is valued at the price estimate from Details."
        descriptionPlaceholder="Describe the proposed scope of work"
        descriptionNote={
          jobDescriptionEmpty
            ? "Optional. The copy action unlocks once the job description has text."
            : "Optional. Copying is one-shot — this field stays independently editable."
        }
        descriptionAction={
          <button
            type="button"
            className={styles.copy}
            onClick={handleCopyFromJob}
            disabled={jobDescriptionEmpty}
            title={
              jobDescriptionEmpty
                ? "Fill in the job description first"
                : "Copy the job description into this field"
            }
          >
            Copy from Job Description
          </button>
        }
      />

      {jobId && jobStatus && (
        <DocumentsSection jobId={jobId} jobStatus={jobStatus} onQuoteAccepted={onQuoteAccepted} />
      )}
    </>
  );
}
